import type { OrganizationInvitationQuery } from '@app/graphql';
import {
  useOrganizationInvitationAcceptMutation,
  useOrganizationInvitationQuery,
  useOrganizationInvitationRejectMutation,
} from '@app/graphql';
import { showToast, ToastContainer } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import { Button } from '@hopin-team/ui-button';
import { Flex } from '@hopin-team/ui-flex';
import { getFromTheme } from '@hopin-team/ui-helper-deep-branding';
import { Text } from '@hopin-team/ui-text';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

type InvitationParsed = {
  organizationName: string;
  invitationId: string;
  role: string;
};

const Container = styled(Flex)`
  background-color: ${() => getFromTheme(`COLOR.GREY[100]`)};
`;

const StyledButton = styled(Button)`
  background-color: ${() => getFromTheme(`COLOR.GREY.WHITE`)};
`;

const getCurrentInvitation = (
  invitationData: OrganizationInvitationQuery | undefined,
): InvitationParsed | null => {
  const edges = invitationData?.me?.organizationInvitations?.edges;

  if (!edges?.length) {
    return null;
  }

  const nextEdge = edges[0];

  if (!nextEdge?.node) {
    return null;
  }

  const {
    id: invitationId,
    role: role,
    organization: { name: organizationName },
  } = nextEdge.node;

  return {
    organizationName,
    invitationId,
    role,
  };
};

const getRoleName = (role: string) => {
  const roleNameMap: Record<string, string> = {
    ADMIN: 'teamInvitation.roleAdmin',
    MEMBER: 'teamInvitation.roleMember',
  };
  return roleNameMap[role] || '';
};

type CurrentAction = 'accept' | 'reject';

const invitationToastContainerId = 'team-invitation-toast-container';

export const TeamInvitationBanner = () => {
  const { t } = useTranslation();
  const [{ data: invitationData }] = useOrganizationInvitationQuery();
  const [currentAction, setCurrentAction] = useState<CurrentAction>();

  const currentInvitation = useMemo(
    () => getCurrentInvitation(invitationData),
    [invitationData],
  );

  const [previousInvitation, setPreviousInvitation] =
    useState(currentInvitation);

  const [
    {
      data: invitationAcceptData,
      fetching: invitationAcceptFetching,
      error: invitationAcceptError,
    },
    acceptInvitation,
  ] = useOrganizationInvitationAcceptMutation();

  const [
    { fetching: invitationRejectFetching, error: invitationRejectError },
    rejectInvitation,
  ] = useOrganizationInvitationRejectMutation();

  useEffect(() => {
    if (!currentInvitation) {
      return;
    }
    if (invitationAcceptFetching || invitationRejectFetching) {
      setPreviousInvitation({
        organizationName: currentInvitation.organizationName,
        invitationId: currentInvitation.invitationId,
        role: currentInvitation.role,
      });
    }
  }, [currentInvitation, invitationAcceptFetching, invitationRejectFetching]);

  useEffect(() => {
    if (!previousInvitation || currentAction !== 'accept') {
      return;
    }
    if (invitationAcceptError) {
      showToast(
        t('teamInvitation.toastAcceptError', {
          'org-name': previousInvitation.organizationName,
        }),
        'error',
        {
          toastId: `${previousInvitation.invitationId}-accept-error`,
          containerId: invitationToastContainerId,
        },
      );
    } else if (invitationAcceptData) {
      showToast(
        t('teamInvitation.toastAcceptSuccess', {
          'org-name': previousInvitation.organizationName,
          role: t(getRoleName(previousInvitation.role)),
        }),
        'success',
        {
          toastId: `${previousInvitation.invitationId}-accept-success`,
          containerId: invitationToastContainerId,
        },
      );
    }
  }, [
    t,
    invitationAcceptError,
    currentAction,
    invitationAcceptData,
    previousInvitation,
  ]);

  useEffect(() => {
    if (!previousInvitation || currentAction !== 'reject') {
      return;
    }
    if (invitationRejectError) {
      showToast(t('teamInvitation.toastDeclineError'), 'error', {
        toastId: `${previousInvitation.invitationId}-reject-error`,
        containerId: invitationToastContainerId,
      });
    }
  }, [t, invitationRejectError, currentAction, previousInvitation]);

  const handleAccept = () => {
    if (!currentInvitation) {
      return;
    }
    setCurrentAction('accept');
    acceptInvitation({
      input: {
        invitationId: currentInvitation.invitationId,
      },
    });
  };

  const handleReject = () => {
    if (!currentInvitation) {
      return;
    }
    setCurrentAction('reject');
    rejectInvitation({
      input: {
        invitationId: currentInvitation.invitationId,
      },
    });
  };

  return (
    <>
      <ToastContainer
        enableMultiContainer
        containerId={invitationToastContainerId}
      />
      {currentInvitation && (
        <Container p={2}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flex="1"
            px={3}
            container={90}
          >
            <Text pattern="strong" mr={2}>
              {t('teamInvitation.invite', {
                'org-name': currentInvitation.organizationName,
                role: t(getRoleName(currentInvitation.role)),
              })}
            </Text>
            <Flex>
              <Button
                disabled={invitationAcceptFetching}
                size="small"
                px={2}
                mr={2}
                onClick={handleAccept}
              >
                {t(
                  invitationAcceptFetching
                    ? 'teamInvitation.acceptFetching'
                    : 'teamInvitation.accept',
                )}
              </Button>
              <StyledButton
                onClick={handleReject}
                disabled={invitationRejectFetching}
                size="small"
                pattern="danger"
                px={2}
                isOutlined
              >
                {t(
                  invitationRejectFetching
                    ? 'teamInvitation.declineFetching'
                    : 'teamInvitation.decline',
                )}
              </StyledButton>
            </Flex>
          </Flex>
        </Container>
      )}
    </>
  );
};
